import type { BoardDto, UserSummary } from '@kanban/shared';
import { PRIORITY_LABELS, type Priority } from '@kanban/shared';
import { useState } from 'react';
import { useSavedViewMutations } from '../lib/boards.js';
import {
  EMPTY_FILTER,
  filterToParams,
  isFilterActive,
  type CardFilter,
  type DueFilter,
} from '../lib/card-filter.js';

const DUE_LABELS: Record<DueFilter, string> = {
  alle: 'Alle',
  ueberfaellig: 'Überfällig',
  heute: 'Heute',
  woche: 'Diese Woche',
  ohne: 'Ohne Datum',
};

/**
 * Filterleiste (SPEC 9.3). Aktive Filter stehen als entfernbare Chips ueber
 * dem Board; die Einstellungen selbst oeffnen auf Mobil als Bottom Sheet.
 */
export function FilterBar({
  board,
  me,
  filter,
  onChange,
  matchCount,
}: {
  board: BoardDto;
  me: UserSummary;
  filter: CardFilter;
  onChange: (next: CardFilter) => void;
  matchCount: number;
}) {
  const [open, setOpen] = useState(false);
  const active = isFilterActive(filter);
  const saveView = useSavedViewMutations();

  const chips: { key: string; label: string; clear: Partial<CardFilter> }[] = [];
  if (filter.assignee !== 'alle') {
    const name =
      filter.assignee === 'ich'
        ? 'Mir zugewiesen'
        : filter.assignee === 'niemand'
          ? 'Niemandem zugewiesen'
          : (board.members.find((m) => m.id === filter.assignee)?.name ?? 'Person');
    chips.push({ key: 'assignee', label: name, clear: { assignee: 'alle' } });
  }
  for (const id of filter.labelIds) {
    const label = board.labels.find((l) => l.id === id);
    chips.push({
      key: `label-${id}`,
      label: label?.name ?? 'Label',
      clear: { labelIds: filter.labelIds.filter((other) => other !== id) },
    });
  }
  if (filter.due !== 'alle') {
    chips.push({ key: 'due', label: DUE_LABELS[filter.due], clear: { due: 'alle' } });
  }
  if (filter.priority !== null) {
    chips.push({
      key: 'priority',
      label: `Priorität ${PRIORITY_LABELS[filter.priority as Priority]}`,
      clear: { priority: null },
    });
  }
  if (filter.query.trim()) {
    chips.push({ key: 'query', label: `„${filter.query.trim()}"`, clear: { query: '' } });
  }
  if (!filter.showCompleted) {
    chips.push({ key: 'done', label: 'Ohne Erledigte', clear: { showCompleted: true } });
  }

  return (
    <>
      <div className="flex items-center gap-2 overflow-x-auto px-4 pb-2">
        <button
          type="button"
          data-testid="open-filter"
          onClick={() => setOpen(true)}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
            active ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-surface'
          }`}
        >
          Filter{active ? ` · ${matchCount}` : ''}
        </button>

        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            data-testid="filter-chip"
            onClick={() => onChange({ ...filter, ...chip.clear })}
            className="shrink-0 rounded-full border border-border bg-surface px-3 py-1.5 text-sm"
            aria-label={`Filter ${chip.label} entfernen`}
          >
            {chip.label} <span aria-hidden="true">×</span>
          </button>
        ))}

        {active && (
          <>
            {/*
              Ansicht sichern (SPEC 9.3). Gespeichert wird genau der
              Query-String, der auch in der URL steht — kein zweites Format
              und damit auch keine zweite Stelle, die bei neuen Filtern
              nachgezogen werden muesste.
            */}
            <button
              type="button"
              data-testid="save-view"
              onClick={() => {
                const name = window.prompt('Name der Ansicht');
                if (!name?.trim()) return;
                saveView.create.mutate({
                  name: name.trim(),
                  query: filterToParams(filter).toString(),
                  boardId: board.id,
                });
              }}
              className="shrink-0 rounded-full border border-border bg-surface px-3 py-1.5 text-sm"
            >
              Ansicht sichern
            </button>

            <button
              type="button"
              data-testid="clear-filter"
              onClick={() => onChange(EMPTY_FILTER)}
              className="shrink-0 px-2 text-sm text-muted underline"
            >
              Zurücksetzen
            </button>
          </>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Filter"
          data-testid="filter-sheet"
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div className="max-h-[85vh] w-full overflow-y-auto rounded-t-[var(--radius-lg)] bg-surface p-4 md:max-w-md md:rounded-[var(--radius-lg)]">
            <h2 className="text-sm font-semibold">Filter</h2>

            <label className="mt-3 block text-xs font-medium text-muted" htmlFor="filter-query">
              Suche in Titel und Beschreibung
            </label>
            <input
              id="filter-query"
              value={filter.query}
              onChange={(event) => onChange({ ...filter, query: event.target.value })}
              className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
              style={{ minHeight: 'var(--touch-target)' }}
            />

            <fieldset className="mt-4">
              <legend className="text-xs font-medium text-muted">Verantwortlich</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {[
                  { id: 'alle', name: 'Alle' },
                  { id: 'ich', name: 'Mir zugewiesen' },
                  { id: 'niemand', name: 'Niemandem' },
                  ...board.members.filter((m) => m.id !== me.id),
                ].map((option) => (
                  <Chip
                    key={option.id}
                    active={filter.assignee === option.id}
                    onClick={() => onChange({ ...filter, assignee: option.id })}
                  >
                    {option.name}
                  </Chip>
                ))}
              </div>
            </fieldset>

            {board.labels.length > 0 && (
              <fieldset className="mt-4">
                <legend className="flex items-center gap-2 text-xs font-medium text-muted">
                  Labels
                  <button
                    type="button"
                    data-testid="label-mode"
                    onClick={() =>
                      onChange({ ...filter, labelMode: filter.labelMode === 'und' ? 'oder' : 'und' })
                    }
                    className="rounded-full border border-border px-2 py-0.5 text-xs"
                  >
                    {filter.labelMode === 'und' ? 'alle (UND)' : 'eines (ODER)'}
                  </button>
                </legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {board.labels.map((label) => (
                    <Chip
                      key={label.id}
                      active={filter.labelIds.includes(label.id)}
                      onClick={() =>
                        onChange({
                          ...filter,
                          labelIds: filter.labelIds.includes(label.id)
                            ? filter.labelIds.filter((id) => id !== label.id)
                            : [...filter.labelIds, label.id],
                        })
                      }
                    >
                      <span
                        className="mr-1 inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: `var(--color-label-${label.color})` }}
                        aria-hidden="true"
                      />
                      {label.name}
                    </Chip>
                  ))}
                </div>
              </fieldset>
            )}

            <fieldset className="mt-4">
              <legend className="text-xs font-medium text-muted">Fälligkeit</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {(Object.keys(DUE_LABELS) as DueFilter[]).map((value) => (
                  <Chip
                    key={value}
                    active={filter.due === value}
                    onClick={() => onChange({ ...filter, due: value })}
                  >
                    {DUE_LABELS[value]}
                  </Chip>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-4">
              <legend className="text-xs font-medium text-muted">Priorität</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                <Chip active={filter.priority === null} onClick={() => onChange({ ...filter, priority: null })}>
                  Alle
                </Chip>
                {([3, 2, 1, 0] as Priority[]).map((value) => (
                  <Chip
                    key={value}
                    active={filter.priority === value}
                    onClick={() => onChange({ ...filter, priority: value })}
                  >
                    {PRIORITY_LABELS[value]}
                  </Chip>
                ))}
              </div>
            </fieldset>

            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filter.showCompleted}
                onChange={(event) => onChange({ ...filter, showCompleted: event.target.checked })}
                className="h-5 w-5 accent-[var(--color-accent)]"
              />
              Erledigte anzeigen
            </label>

            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)]"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                {matchCount} Karten anzeigen
              </button>
              <button
                type="button"
                onClick={() => onChange(EMPTY_FILTER)}
                className="rounded-[var(--radius)] border border-border px-4 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-3 text-sm ${
        active ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-surface'
      }`}
      style={{ minHeight: 'var(--touch-target)' }}
    >
      {children}
    </button>
  );
}
